import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, ArrowUpLeft } from "lucide-react";
import { Reveal, SectionHeader } from "./Reveal";
import { PROJECTS as STATIC_PROJECTS, resolveMedia } from "../data/projects";
import { PROJECT_EN, GALLERY_EN } from "../i18n";
import { useLang } from "../context/LangContext";
import ProjectDetail from "./ProjectDetail";

export default function Projects() {
  const { lang, t } = useLang();
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/projects`)
      .then((r) => (r.ok ? r.json() : []))
      .then((d) => setRemote(Array.isArray(d) ? d : []))
      .catch(() => setRemote([]));
  }, []);

  const localize = (p) => {
    if (lang !== "en") return p;
    const en = p.en && p.en.title ? p.en : PROJECT_EN[p.title];
    if (!en) return p;
    return {
      ...p,
      title: en.title,
      categoryLabel: en.categoryLabel || p.categoryLabel,
      location: en.location || p.location,
      client: en.client || p.client,
      excerpt: en.excerpt || p.excerpt,
      story: en.story && en.story.length ? en.story : p.story,
      highlights: en.highlights && en.highlights.length ? en.highlights : p.highlights,
      gallery: (p.gallery || []).map((g) => ({
        ...g,
        caption: p.en && p.en.title ? `${en.title} — site photo` : GALLERY_EN[g.caption] || g.caption,
      })),
    };
  };

  const source = (remote && remote.length ? remote : STATIC_PROJECTS).map((p) => ({
    ...p,
    image: resolveMedia(p.image),
    gallery: (p.gallery || []).map((g) => ({ ...g, src: resolveMedia(g.src) })),
  })).map(localize);
  const visible = source.filter((p) => filter === "all" || p.category === filter);

  const FILTERS = [
    { id: "all", label: t.projects.filters.all },
    { id: "roads", label: t.projects.filters.roads },
    { id: "water", label: t.projects.filters.water },
    { id: "buildings", label: t.projects.filters.buildings },
  ];

  return (
    <section id="projects" data-testid="projects-section" className="relative py-20 lg:py-32 bg-white/40 border-y border-[#E8DFC5]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <SectionHeader
          index={t.projects.index}
          eyebrow={t.projects.eyebrow}
          title={t.projects.title}
          description={t.projects.desc}
        />

        <Reveal className="flex flex-wrap gap-3 mb-12">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              data-testid={`project-filter-${f.id}`}
              onClick={() => setFilter(f.id)}
              className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-300 ${
                filter === f.id
                  ? "bg-emerald-500 text-white shadow-[0_0_20px_rgba(16,185,129,0.35)]"
                  : "border border-[#E8DFC5] text-stone-600 hover:border-emerald-500/60 hover:text-emerald-600"
              }`}
            >
              {f.label}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.article
                layout
                key={p.id}
                data-testid={`project-card-${p.id}`}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelected(p)}
                className="group relative rounded-lg overflow-hidden border border-[#E8DFC5] bg-white cursor-pointer transition-colors duration-300 hover:border-emerald-500/50"
              >
                <div className="relative aspect-[16/10] overflow-hidden clip-frame">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#14100A] via-[#14100A]/30 to-transparent" />
                  <span className="absolute top-5 right-5 rounded-full bg-[#14100A]/70 backdrop-blur-md border border-emerald-500/40 px-4 py-1.5 text-xs font-semibold text-[#6EE7B7]">
                    {p.categoryLabel}
                  </span>
                </div>
                <div className="p-6 lg:p-7">
                  <h3 className="font-heading font-bold text-xl text-stone-900 mb-3 transition-colors duration-300 group-hover:text-emerald-600">
                    {p.title}
                  </h3>
                  <p className="flex items-center gap-2 text-sm text-stone-500 mb-3">
                    <MapPin className="w-4 h-4 text-emerald-500 shrink-0" />
                    {p.location}
                  </p>
                  <p className="text-sm leading-relaxed text-stone-500">{p.excerpt}</p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-emerald-600">
                    {t.projects.storyLink}
                    <ArrowUpLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                  </span>
                </div>
              </motion.article>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && <ProjectDetail project={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </section>
  );
}
